package com.essntl.uikit.components.datepicker

import androidx.compose.foundation.layout.padding
import androidx.compose.material3.DatePickerDialog
import androidx.compose.material3.DateRangePicker
import androidx.compose.material3.DisplayMode
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.rememberDateRangePickerState
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.button.destructive.EsLargeDestructiveButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.button.secondary.EsLargeSecondaryButton
import core.dateformat.domain.DateFormatUseCase
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalDateTime.Companion
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import org.koin.compose.koinInject

/**
 * Date range picker dialog.
 *
 * @param isExpanded whether the dialog is expanded or not.
 * @param onDismissRequest callback when the dialog is dismissed.
 * @param fromDate the from date, in the format of "MM/DD/YYYY".
 * @param toDate the to date, in the format of "MM/DD/YYYY".
 * @param onDateRangeChange callback when the date range is changed, in the format of "MM/DD/YYYY".
 * @param modifier the modifier.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EsDateRangePickerDialog(
    isExpanded: Boolean,
    onDismissRequest: () -> Unit,
    fromDate: String?,
    toDate: String?,
    onDateRangeChange: (String, String) -> Unit,
    modifier: Modifier = Modifier,
) {
    val datePickerColors = rememberDatePickerColors()

    val dateFormatUseCase = koinInject<DateFormatUseCase>()

    println("EsDateRangePickerDialog $fromDate $toDate")

    if (isExpanded) {
        val timeZone = TimeZone.UTC

        val initialStartDateMillis = try {
            fromDate?.let {
                LocalDateTime.parse("${it}T22:12:00")
                    .toInstant(timeZone)
                    .toEpochMilliseconds()
            }
        } catch (e: Exception) {
            null
        }

        val initialEndDateMillis = try {
            toDate?.let {
                LocalDateTime.parse("${it}T22:12:00")
                    .toInstant(timeZone)
                    .toEpochMilliseconds()
            }
        } catch (e: Exception) {
            null
        }

        // Ensure consistency: End date cannot exist without a start date
        val consistentStartDateMillis = initialStartDateMillis
        val consistentEndDateMillis = if (initialStartDateMillis == null) null else initialEndDateMillis

        val datePickerState =
            rememberDateRangePickerState(
                initialSelectedStartDateMillis = consistentStartDateMillis,
                initialSelectedEndDateMillis = consistentEndDateMillis,
                initialDisplayMode = DisplayMode.Picker,
            )

        DatePickerDialog(
            onDismissRequest = onDismissRequest,
            confirmButton = {
                EsLargePrimaryButton(
                    onClick = {
                        val from = datePickerState.selectedStartDateMillis?.let { millis ->
                            dateFormatUseCase.formatDateDialog(millis)
                        }

                        val to = datePickerState.selectedEndDateMillis?.let { millis ->
                            dateFormatUseCase.formatDateDialog(millis)
                        }

                        onDateRangeChange(
                            from.orEmpty(),
                            to.orEmpty(),
                        )

                        onDismissRequest()
                    },
                    enabled = datePickerState.selectedStartDateMillis != null &&
                        datePickerState.selectedEndDateMillis != null,
                    text = "Done",
                )
            },
            dismissButton = {
                EsLargeDestructiveButton(
                    onClick = {
                        onDateRangeChange("", "")
                        onDismissRequest()
                    },
                    text = "Clear",
                )

                EsLargeSecondaryButton(
                    onClick = {
                        onDismissRequest()
                    },
                    text = "Cancel",
                )
            },
            colors = datePickerColors,
            modifier = modifier,
        ) {
            DateRangePicker(
                state = datePickerState,
                showModeToggle = false,
                colors = datePickerColors,
                modifier =
                    Modifier
                        .weight(1f)
                        .padding(
                            vertical = 12.dp,
                        ),
            )
        }
    }
}